/** @jsx jsx */
import type { ComponentType, ReactNode } from 'react';
import { cssMap, jsx, styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants';
import type { ErrorBoundaryFallbackComponent } from '@atlassian/jira-error-boundary';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import {
	NavErrorBoundary,
	getRelevantFFlagsForObservability,
} from '@atlassian/jira-navigation-kit-error-boundary';
import { ProjectViewNavigationSkipLink } from '../page-container/page-container-skip-links';

type Props = {
	children: ReactNode;
	navComponent: ComponentType;
};

/** this component is for INTERNAL DOGFOODING only
 * https://hello.jira.atlassian.cloud/browse/SEA-2744
 * It should NEVER be shown to customers.
 * TODO cleanup as part of FF cleanup sea-2744-jsw-tabs-nav
 */
export const SEA2744DogfoodingErrorFallback: ErrorBoundaryFallbackComponent = ({ error }) => {
	// client injected data = risky
	// BUT will never be evaluated (React is escaping by default)
	const REPORT = `
Error Class: ${error.name}
Error Message: ${error.message}
hostname: ${window.location.hostname}
pathname: ${window.location.pathname}
search: ${window.location.search}
FFlags:
${getRelevantFFlagsForObservability()
	.map(([key, val]) => `- ${key} = ${val}`)
	.join('\n')}
Full Error: (see console)
`;

	return (
		<SEA2744DogfoodingErrorFallbackWrapper>
			<h3>
				Congrats, you found a bug 👏<sub>(only Atlassians can see this message)</sub>
			</h3>
			<p>
				Please{' '}
				<a target="_blank" href="https://go.atlassian.com/nav-v4-dogfooding/#Report-a-bug">
					report it ASAP
				</a>
				↗. Thanks from the Nav team!{' '}
				<button onClick={() => window.location.reload()}>Reload page</button>
			</p>
			{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
			<details
				onClick={() => {
					// eslint-disable-next-line no-console -- only active in internal dogfooding, to ease reporting
					console.error('Please report this crash to the Navigation Team if requested:', REPORT, {
						error,
					});
				}}
			>
				<summary>Error details...</summary>
				<p>
					<code>
						<pre>{REPORT.trim()}</pre>
					</code>
				</p>
			</details>
		</SEA2744DogfoodingErrorFallbackWrapper>
	);
};

const ErrorBoundaryFallback: ErrorBoundaryFallbackComponent = (props) => {
	if (ff('atlassian-dogfooding-only')) {
		return <SEA2744DogfoodingErrorFallback {...props} />;
	}

	// null = equivalent of "unmount"
	// Maybe we could do more graceful degradation?
	// Let's review this after dogfooding:
	// Tracked https://hello.jira.atlassian.cloud/browse/SEA-3059
	return null;
};

const HorizontalNavWrapperOld = ({ children, navComponent: HorizontalNavComponent }: Props) => {
	const isJwmStorageUpgradeMainEnabled = ff('jwm.storage-upgrade-main');

	return (
		<ThemedContainer isOverflowHidden={isJwmStorageUpgradeMainEnabled}>
			<NavErrorBoundary
				packageName="page-container"
				id="spa-horizontal-nav"
				fallback={ErrorBoundaryFallback}
			>
				<NavErrorBoundary
					packageName="page-container"
					id="projectNavigationSkipLink"
					fallback="unmount"
				>
					<ProjectViewNavigationSkipLink />
				</NavErrorBoundary>
				<HorizontalNavComponent />
			</NavErrorBoundary>

			{children}
		</ThemedContainer>
	);
};

const wrapperStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		background: Tokens.ELEVATION_SURFACE,
		backgroundBlendMode: 'darken',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	preventOverflow: {
		overflow: 'hidden',
	},
});

const HorizontalNavWrapperNew = ({ children, navComponent: HorizontalNavComponent }: Props) => {
	const isJwmStorageUpgradeMainEnabled = ff('jwm.storage-upgrade-main');

	return (
		<div
			css={[wrapperStyles.root, isJwmStorageUpgradeMainEnabled && wrapperStyles.preventOverflow]}
		>
			<NavErrorBoundary
				packageName="page-container"
				id="spa-horizontal-nav"
				fallback={ErrorBoundaryFallback}
			>
				<NavErrorBoundary
					packageName="page-container"
					id="projectNavigationSkipLink"
					fallback="unmount"
				>
					<ProjectViewNavigationSkipLink />
				</NavErrorBoundary>
				<HorizontalNavComponent />
			</NavErrorBoundary>

			{children}
		</div>
	);
};

export const HorizontalNavWrapper = componentWithFF(
	'platform.atlassian.griffin-new-page-layout',
	HorizontalNavWrapperNew,
	HorizontalNavWrapperOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ThemedContainer = styled.div<{ isOverflowHidden: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: Tokens.ELEVATION_SURFACE,
	// when image background is applied, the image will be darkened in dark mode
	// by blending with a black transparent background
	backgroundBlendMode: 'darken',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	overflow: ({ isOverflowHidden }) => (isOverflowHidden ? 'hidden' : ''),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SEA2744DogfoodingErrorFallbackWrapper = styled.div({
	borderColor: token('color.text.danger'),
	borderWidth: '2px',
	borderStyle: 'dashed',
});
